import { useEffect, useRef } from "react";

/**
 * Click-and-drag horizontal scrolling for an overflowing container.
 *
 * Mouse only: touch and pen already pan a scroll container natively, with the platform's own
 * inertia and rubber-banding, so hijacking them would only make things worse. `pointerType`
 * is the gate rather than a media query, because a laptop with a touchscreen is both.
 *
 * The tables this drives are full of buttons and links, so a press only becomes a drag once
 * the pointer has travelled DRAG_THRESHOLD px — below that it stays an ordinary click. Once
 * it does become a drag, the click that browsers fire on release is swallowed in the capture
 * phase, so letting go on top of a row's action button doesn't trigger it.
 *
 * State that changes per-frame is written straight to the DOM (`scrollLeft`, and the two data
 * attributes the stylesheet hooks its cursor onto) instead of through React state: a table can
 * render hundreds of cells, and re-rendering them on pointerdown/up to flip a cursor is not
 * worth it.
 */
const DRAG_THRESHOLD = 5;

export const useDragScroll = <T extends HTMLElement>() => {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // `data-draggable` drives the grab cursor, so it has to track whether there is actually
    // anything to scroll — the schema, the column widths and the viewport all move it.
    const syncOverflow = () => {
      // +1 absorbs the sub-pixel difference a fractional layout width leaves behind.
      const overflowing = el.scrollWidth > el.clientWidth + 1;
      if (overflowing) {
        el.dataset.draggable = "true";
      } else {
        delete el.dataset.draggable;
      }
    };

    syncOverflow();
    const observer = new ResizeObserver(syncOverflow);
    observer.observe(el);
    // The inner table is what actually grows when rows or columns arrive; the container's own
    // box often stays put.
    if (el.firstElementChild) observer.observe(el.firstElementChild);

    const drag = {
      pointerId: -1,
      startX: 0,
      startScrollLeft: 0,
      armed: false,
      dragging: false,
      swallowClick: false,
    };

    const endDrag = () => {
      if (drag.dragging) {
        delete el.dataset.dragging;
        // Consumed by onClickCapture below, on the click that release is about to produce.
        drag.swallowClick = true;
      }
      drag.armed = false;
      drag.dragging = false;
      drag.pointerId = -1;
    };

    const onPointerDown = (e: PointerEvent) => {
      // Left button only, and never on a scrollbar drag — the browser handles that itself.
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      if (!el.dataset.draggable) return;
      drag.swallowClick = false;
      drag.pointerId = e.pointerId;
      drag.startX = e.clientX;
      drag.startScrollLeft = el.scrollLeft;
      drag.armed = true;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!drag.armed || e.pointerId !== drag.pointerId) return;
      // The button was released somewhere we never saw the pointerup (outside the window).
      if (e.buttons === 0) {
        endDrag();
        return;
      }

      const dx = e.clientX - drag.startX;
      if (!drag.dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        drag.dragging = true;
        el.dataset.dragging = "true";
        // The press may already have started a text selection before it crossed the
        // threshold; drop it, or the drag paints a selection across the row.
        window.getSelection()?.removeAllRanges();
      }

      e.preventDefault();
      el.scrollLeft = drag.startScrollLeft - dx;
    };

    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerId !== drag.pointerId) return;
      endDrag();
    };

    const onClickCapture = (e: MouseEvent) => {
      if (!drag.swallowClick) return;
      drag.swallowClick = false;
      e.stopPropagation();
      e.preventDefault();
    };

    // Cells hold images and links, both natively draggable — without this the browser starts
    // its own drag-and-drop and the scroll never moves.
    const onDragStart = (e: DragEvent) => {
      if (drag.armed || drag.dragging) e.preventDefault();
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("click", onClickCapture, true);
    el.addEventListener("dragstart", onDragStart);
    // Move/up live on the window so a drag survives the pointer leaving the container.
    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);

    return () => {
      observer.disconnect();
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("click", onClickCapture, true);
      el.removeEventListener("dragstart", onDragStart);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };
  }, []);

  return ref;
};

export default useDragScroll;
